import MaterialReactTable from 'material-react-table'
import EditSharpIcon from '@mui/icons-material/EditSharp'
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import ArticleTwoToneIcon from '@mui/icons-material/ArticleTwoTone';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import { useMemo, useRef, useCallback } from "react";
import { Box, Button } from '@mui/material';
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import ReportExportDropdown from './ReportExportDropdown';
import { createTableExportHandlers, sanitizeExportFilename } from '../utils/tableExportHandlers';

const ACTION_BUTTON_WIDTH = 26;

const isStatusColumn = (col) => {
    const name = (col?.headerName || '').toLowerCase();
    return name === 'status' || name === 'record status' || col?.fieldName === 'status_2';
};

const displayCellText = (col, value) => {
    const text = value == null ? '' : String(value);
    if (!text || text === 'undefined') {
        return '';
    }
    return isStatusColumn(col) ? text.replace(/_/g, ' ') : text;
};

const measureTextWidth = (value, charPx) => {
    const text = value == null ? '' : String(value);
    if (!text || text === 'undefined') {
        return 0;
    }
    return Math.ceil(text.length * charPx) + 28;
};

const fitColumnSize = (col, rows) => {
    const headerWidth = measureTextWidth(col.headerName, 8.2);
    let contentWidth = 0;
    const sample = rows || [];
    const limit = Math.min(sample.length, 80);
    for (let index = 0; index < limit; index += 1) {
        const value = sample[index]?.[col.fieldName];
        if (value == null || typeof value === 'object') {
            continue;
        }
        contentWidth = Math.max(contentWidth, measureTextWidth(displayCellText(col, value), 8));
    }
    const buttonPad = isStatusColumn(col) ? 36 : 0;
    return Math.max(72, headerWidth, contentWidth + buttonPad);
};

const countRowActionButtons = ({
    rowData,
    hideDelete,
    hidePrint,
    hasBrPrintHandler,
    hasInvoicePrintHandler,
}) => {
    let count = 0;

    if (rowData?.hide_row_edit === undefined || rowData?.hide_row_edit === "No") {
        count += 1;
    }
    if (!hideDelete && (rowData?.hide_row_delete === undefined || rowData?.hide_row_delete === "No")) {
        count += 1;
    }
    if (
        hasBrPrintHandler &&
        rowData?.is_batch_report_needed === "Yes" &&
        rowData?.hide_row_print === "No"
    ) {
        count += 1;
    }
    if (!hidePrint && (rowData?.hide_row_print === undefined || rowData?.hide_row_print === "No")) {
        count += 1;
    }
    if (hasInvoicePrintHandler && rowData?.invoice_id && !rowData?.is_inactive) {
        count += 1;
    }

    return count;
};

const getActionColumnWidth = ({ data, hideDelete, hidePrint, hasBrPrintHandler, hasInvoicePrintHandler }) => {
    const minButtons = countRowActionButtons({
        rowData: {},
        hideDelete,
        hidePrint,
        hasBrPrintHandler,
        hasInvoicePrintHandler,
    });

    const maxRowButtons = (data || []).reduce((max, row) => {
        const rowCount = countRowActionButtons({
            rowData: row,
            hideDelete,
            hidePrint,
            hasBrPrintHandler,
            hasInvoicePrintHandler,
        });
        return Math.max(max, rowCount);
    }, minButtons);

    const buttonCount = Math.max(maxRowButtons, 1);
    return buttonCount * ACTION_BUTTON_WIDTH + 20;
};

const Status = ({value, row, onCompletedStatusClick, onToCompleteStatusClick}) => {
    const navigate = useNavigate();
    const safeValue = typeof value === "string" ? value : "";

    const handleClick = () => {
        if (onToCompleteStatusClick) {
            onToCompleteStatusClick(row.original.id, row);
            return;
        }
        Swal.fire({title: 'You cannot edit delivery challans related to this work schedule further..Do you wish to confirm?',
        showCancelButton: true,
        confirmButtonText: `Yes`,
        cancelButtonText: `No`,
        }).then((result) => {
        if (result.isConfirmed) {
            navigate(`/JobCompletion/${row.original.id}/`);
        }
        });
    };

    const handleCompletedClick = () => {
        if (onCompletedStatusClick) {
            onCompletedStatusClick(row.original.id, row);
            return;
        }
        navigate(`/JobCompletionReport/${row.original.id}/`);
    };

    const colorMap = {
        not_started: 'warning',
        in_progress: 'primary',
        to_complete: 'success',
        completed: 'error'
    };
    const buttonColor = colorMap[safeValue];

    const transformedValue = safeValue.includes('_') ? safeValue.replace('_', ' ') : safeValue;

return (
    safeValue === 'in_progress' || safeValue === 'not_started' || safeValue === 'completed' || safeValue ==='to_complete'?
        (<Button
            color={buttonColor} variant="contained" size="small"
            sx={{ whiteSpace: 'nowrap', minWidth: 'max-content', px: 1.25, lineHeight: 1.2 }}
            onClick={
                safeValue === 'to_complete' ? handleClick
                : safeValue === 'completed' ? handleCompletedClick
                : undefined
            }
        >{transformedValue}
        </Button>)
        :
        (<Box component="span"
            sx={{
                backgroundColor:
                    safeValue.toLowerCase() === 'active'? 'success.dark'
                        : (safeValue.toLowerCase() === 'inactive' || safeValue.toLowerCase() === 'cancelled')
                            ? 'error.dark': 'warning.dark',
                borderRadius: '0.25rem',
                color: '#fff',
                whiteSpace: 'nowrap',
                p: '0.25rem',
                textTransform: 'capitalize',
                fontWeight: 'bold'
            }}
        >{safeValue}
        </Box>)
    );
}

function ReactTableWrapper({
    title,
    columns,
    data,
    onRowEdit,
    onRowDelete,
    onCSVExport,
    onExcelExport,
    onPDFExport,
    onImageExport,
    exportFilename,
    enableExportToolbar = true,
    isLoading = true,
    hideDelete = false,
    hidePrint = true,
    onRowBrPrint,
    onRowPrint,
    onRowInvoicePrint,
    onRowConvert,
    onCompletedStatusClick,
    onToCompleteStatusClick,
    left_pinned,
    right_pinned,
    compactColumns = false,
    enableSorting = true,
    getRowSx,
    centerHeaders = false,
    normalBody = false,
}) {

    const handlersRef = useRef({});
    handlersRef.current = { onRowEdit, onRowDelete, onRowBrPrint, onRowPrint, onRowInvoicePrint, onRowConvert, onCompletedStatusClick, onToCompleteStatusClick };
    const tableExportRef = useRef(null);
    const exportFileBase = sanitizeExportFilename(exportFilename || title || 'export');

    const defaultExportHandlers = useMemo(
        () => createTableExportHandlers({
            columns,
            records: data || [],
            title: title || 'Report',
            filename: exportFileBase,
            tableElementRef: tableExportRef,
        }),
        [columns, data, exportFileBase, title],
    );

    const handleExcelExport = useCallback(() => {
        if (onExcelExport) {
            onExcelExport();
            return;
        }
        defaultExportHandlers.onExcel();
    }, [defaultExportHandlers, onExcelExport]);

    const handlePDFExport = useCallback(() => {
        if (onPDFExport) {
            onPDFExport();
            return;
        }
        defaultExportHandlers.onPDF();
    }, [defaultExportHandlers, onPDFExport]);

    const handleImageExport = useCallback(async () => {
        try {
            if (onImageExport) {
                await onImageExport();
                return;
            }
            await defaultExportHandlers.onImage();
        } catch (error) {
            Swal.fire(error?.message || 'Image export failed.', '', 'error');
        }
    }, [defaultExportHandlers, onImageExport]);

    const exportDisabled = isLoading || !(data || []).length;
    const hasBrPrintHandler = Boolean(onRowBrPrint);
    const hasInvoicePrintHandler = Boolean(onRowInvoicePrint);
    const hasConvertHandler = Boolean(onRowConvert);

    const compactCellSx = {
        padding: compactColumns ? '2px 6px' : '2px 8px',
        fontSize: compactColumns ? '12px' : '13px',
        whiteSpace: 'nowrap',
        overflow: 'visible',
        textOverflow: 'clip',
        verticalAlign: 'middle',
    };

    const centeredHeadSx = useMemo(() => ({
        // Headers stay centered on every table. centerHeaders remains for existing callers.
        textAlign: 'center',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
        lineHeight: 1.25,
        overflow: 'visible',
        textOverflow: 'clip',
        '& .Mui-TableHeadCell-Content': {
            justifyContent: 'center',
            alignItems: 'center',
            whiteSpace: 'nowrap',
        },
        '& .Mui-TableHeadCell-Content-Labels': {
            justifyContent: 'center',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            overflow: 'visible',
            textOverflow: 'clip',
        },
        '& .Mui-TableHeadCell-Content-Wrapper': {
            textAlign: 'center',
            whiteSpace: 'nowrap',
            overflow: 'visible',
            textOverflow: 'clip',
        },
    }), [centerHeaders]);

    const headColorSx = useMemo(() => ({
        background: '#f39c12',
        backgroundColor: '#f39c12',
        color: '#ffffff',
        borderBottom: '1px solid #e67e22',
        '& .MuiTableSortLabel-root, & .Mui-TableHeadCell-Content, & .Mui-TableHeadCell-Content-Labels, & .Mui-TableHeadCell-Content-Wrapper': {
            color: '#ffffff',
        },
        '& .MuiTableSortLabel-icon': {
            color: '#ffffff !important',
        },
    }), []);

    const actionColumnWidth = useMemo(
        () => getActionColumnWidth({ data, hideDelete, hidePrint, hasBrPrintHandler, hasInvoicePrintHandler }),
        [data, hideDelete, hidePrint, hasBrPrintHandler, hasInvoicePrintHandler]
    );

    const tableColumns = useMemo(() => columns.map((col) => {

            let cellAlign = col.align || 'left';
            const fittedSize = col.headerName === 'ROW_ACTIONS' ? null : fitColumnSize(col, data);
            let columnProperties = {
                accessorKey: col.fieldName,
                header: col.headerName,
                ...(fittedSize ? {
                    size: fittedSize,
                    minSize: Math.min(72, fittedSize),
                    maxSize: Math.max(fittedSize, 420),
                } : {}),
            };


            if (col.headerName === 'ROW_ACTIONS') {
                columnProperties = {
                    ...columnProperties,
                    header: 'Action',
                    Header: <span style={col.headerStyle}>Action</span>,
                    size: actionColumnWidth,
                    minSize: actionColumnWidth,
                    maxSize: actionColumnWidth,
                    enableResizing: false,
                    muiTableBodyCellProps: {
                        sx: {
                            whiteSpace: 'nowrap',
                            width: `${actionColumnWidth}px`,
                            maxWidth: `${actionColumnWidth}px`,
                            padding: '1px 4px',
                        },
                    },
                    muiTableHeadCellProps: {
                        sx: {
                            fontWeight: 'bold',
                            fontSize: '15px',
                            ...headColorSx,
                            whiteSpace: 'nowrap',
                            width: `${actionColumnWidth}px`,
                            maxWidth: `${actionColumnWidth}px`,
                            ...centeredHeadSx,
                        },
                    },
                    Cell: ({ row }) => {
                        const currentRowData = row.original;
                        const rowId = currentRowData.id;
                        const is_br_needed = currentRowData?.is_batch_report_needed;
                        const hide_row_edit = currentRowData?.hide_row_edit;
                        const hide_row_delete = currentRowData?.hide_row_delete;
                        const hide_row_print = currentRowData?.hide_row_print;

                        return (
                            <span style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                                {(hide_row_edit == undefined ||  hide_row_edit === "No") && (
                                <EditSharpIcon onClick={(event) => handlersRef.current.onRowEdit(rowId, event)}
                                sx={{
                                    cursor: 'pointer',
                                    color: 'white',
                                    fontSize: '18px',
                                    backgroundColor: 'primary.dark',
                                    margin: '1px',
                                    borderRadius: '4px',
                                    padding: '1px'
                                }}
                                />
                                )}
                                {((!hideDelete) && (hide_row_delete == undefined ||  hide_row_delete === "No")?
                                <DeleteForeverRoundedIcon onClick={(event) => handlersRef.current.onRowDelete(rowId, event)}
                                sx={{
                                    cursor: 'pointer',
                                    color: 'white',
                                    fontSize: '18px',
                                    backgroundColor: 'error.dark',
                                    margin: '1px',
                                    borderRadius: '4px',
                                    padding: '1px'
                                }}
                                />
                                : null)}

                                {((is_br_needed !== undefined) && (is_br_needed == 'Yes') && (hide_row_print === "No")) &&
                                    <ArticleTwoToneIcon  onClick={(event) => handlersRef.current.onRowBrPrint(rowId, event)}sx={{
                                        cursor: 'pointer',
                                        color: 'white',
                                        fontSize: '18px',
                                        backgroundColor: 'warning.dark',
                                        margin: '1px',
                                        borderRadius: '4px',
                                        padding: '1px'
                                    }}/>
                                }
                                 {((!hidePrint) && (hide_row_print == undefined ||  hide_row_print === "No")?
                                <PrintIcon  onClick={(event) => handlersRef.current.onRowPrint(rowId, event)}sx={{
                                    cursor: 'pointer',
                                    color: 'white',
                                    fontSize: '18px',
                                    backgroundColor: 'success.dark',
                                    margin: '1px',
                                    borderRadius: '4px',
                                    padding: '1px'
                                }}/>:null)}
                                {hasInvoicePrintHandler && currentRowData?.invoice_id && !currentRowData?.is_inactive ? (
                                <ReceiptLongOutlinedIcon
                                    titleAccess="Print Sales Invoice"
                                    onClick={(event) => handlersRef.current.onRowInvoicePrint(currentRowData.invoice_id, event)}
                                    sx={{
                                        cursor: 'pointer',
                                        color: 'white',
                                        fontSize: '18px',
                                        backgroundColor: 'info.dark',
                                        margin: '1px',
                                        borderRadius: '4px',
                                        padding: '1px',
                                    }}
                                />
                                ) : null}
                                {hasConvertHandler ? (
                                <RequestQuoteOutlinedIcon
                                    titleAccess="Convert to Quotation"
                                    onClick={(event) => handlersRef.current.onRowConvert(rowId, event)}
                                    sx={{
                                        cursor: 'pointer',
                                        color: 'white',
                                        fontSize: '18px',
                                        backgroundColor: 'info.dark',
                                        margin: '1px',
                                        borderRadius: '4px',
                                        padding: '1px',
                                    }}
                                />
                                ) : null}
                            </span>
                        );
                    }
                }

            } else if (
                (col.headerName || '').toLowerCase() === 'status'
                || col.fieldName === 'status_2'
                || (col.headerName || '').toLowerCase() === 'record status'
            ) {

                columnProperties = {
                    ...columnProperties,
                    header: col.headerName,
                    Cell: ({renderedCellValue,row}) => {

                        return (
                            <div style={{textAlign: cellAlign}}>
                                <Status
                                    value={renderedCellValue}
                                    row={row}
                                    onCompletedStatusClick={handlersRef.current.onCompletedStatusClick}
                                    onToCompleteStatusClick={handlersRef.current.onToCompleteStatusClick}
                                />
                            </div>
                        );
                    },
                    muiTableBodyCellProps: {
                        sx: compactCellSx,
                    },
                    muiTableHeadCellProps: {
                        sx: {
                            fontWeight: 'bold',
                            fontSize: compactColumns ? '12px' : '14px',
                            ...headColorSx,
                            ...centeredHeadSx,
                        },
                    },
                };

            } else {

                if (col.headerStyle) {
                    columnProperties = {
                        ...columnProperties,
                        Header: <span style={col.headerStyle}>{col.headerName}</span>,
                    };
                }

                columnProperties = {
                    ...columnProperties,
                    Cell: ({renderedCellValue, row}) => {
                        const dynamicStyle = col.getCellStyle ? col.getCellStyle(row.original) : undefined;
                        const combinedStyle = (col.dataStyle || dynamicStyle)
                            ? { ...col.dataStyle, ...dynamicStyle }
                            : undefined;

                        return (
                            <div style={{
                                textAlign: cellAlign,
                                whiteSpace: 'nowrap',
                            }}>
                                {col.onCellClick ? (
                                    <button
                                        type="button"
                                        onClick={() => col.onCellClick(row.original)}
                                        style={{
                                            ...(combinedStyle || {}),
                                            background: 'none',
                                            border: 'none',
                                            padding: 0,
                                            cursor: 'pointer',
                                            font: 'inherit',
                                        }}
                                    >
                                        {renderedCellValue === null || renderedCellValue === undefined || renderedCellValue === 'undefined'
                                            ? ''
                                            : renderedCellValue}
                                    </button>
                                ) : (
                                    combinedStyle
                                        ? <span style={combinedStyle}>
                                            {renderedCellValue === null || renderedCellValue === undefined || renderedCellValue === 'undefined'
                                                ? ''
                                                : renderedCellValue}
                                          </span>
                                        : (renderedCellValue === null || renderedCellValue === undefined || renderedCellValue === 'undefined'
                                            ? ''
                                            : renderedCellValue)
                                )}
                            </div>
                        );
                    },
                    muiTableBodyCellProps: {
                        sx: compactCellSx,
                    },
                    muiTableHeadCellProps: {
                        sx: {
                            fontWeight: 'bold',
                            fontSize: compactColumns ? '12px' : '14px',
                            ...headColorSx,
                            ...centeredHeadSx,
                        },
                    },
                };
            }

            return columnProperties;
        }), [columns, data, hideDelete, hidePrint, actionColumnWidth, compactColumns, hasInvoicePrintHandler, centeredHeadSx, headColorSx]);

    return (
        <div ref={tableExportRef} className="table-export-capture" style={{paddingTop: "10px", paddingBottom: "10px"}}>
            <h1 style={{fontSize: '22px', color: 'darkblue', fontWeight: 'bold', display:'none'}}>{title}</h1>
            <MaterialReactTable
                columns={tableColumns}
                data={data}
                layoutMode="semantic"
                enablePinning
                enableStickyHeader
                enableColumnResizing
                columnResizeMode="onEnd"
                enableColumnActions={false}
                enableColumnFilter={false}
                enablePagination={true}
                enableSorting={enableSorting}
                enableBottomToolbar={true}
                enableTopToolbar={true}
                muiTableBodyRowProps={({ row }) => {
                    const inactiveRowSx = getRowSx?.(row.original) || (
                        row.original?.is_inactive
                            ? {
                                backgroundColor: '#fecaca !important',
                                '&:hover': { backgroundColor: '#fca5a5 !important' },
                            }
                            : {}
                    );

                    return {
                        hover: true,
                        sx: inactiveRowSx,
                    };
                }}
                enableDensityToggle={!compactColumns}
                state={{isLoading: isLoading}}
                defaultColumn={{
                    minSize: 72,
                    maxSize: 420,
                    size: 120,
                }}
                muiTablePaperProps={{
                    elevation: 0,
                    sx: {
                      borderRadius: '12px',
                      border: '1px solid #000000',
                      overflow: 'hidden',
                    },
                  }}
                muiTopToolbarProps={{
                    sx: {
                      overflow: 'visible',
                    },
                  }}
                  muiTableBodyProps={{
                    sx: () => ({
                      '& tr:nth-of-type(odd)': {
                        backgroundColor: normalBody ? '#ffffff' : '#bcbfc4',
                      },
                      ...(normalBody ? {
                        backgroundColor: '#ffffff',
                        '& tr': {
                          backgroundColor: '#ffffff',
                        },
                      } : {}),
                      '& td': compactColumns ? {
                        paddingTop: '1px',
                        paddingBottom: '1px',
                        paddingLeft: '6px',
                        paddingRight: '6px',
                        fontSize: '12px',
                        lineHeight: 1.15,
                      } : {
                        paddingTop: '1px',
                        paddingBottom: '1px',
                        lineHeight: 1.15,
                      },
                    }),
                  }}
                muiTableHeadCellProps={{
                    sx: {
                      fontWeight: 'bold',
                      fontSize: compactColumns ? '12px' : '14px',
                      ...headColorSx,
                      ...centeredHeadSx,
                    },
                  }}
                initialState={
                    {
                        density: "compact",
                        pagination: {pageIndex: 0, pageSize:5},
                        // Freeze the Action column (accessor "id") on the right by default
                        columnPinning: {
                            left: left_pinned || [],
                            right: right_pinned !== undefined ? right_pinned : ['id'],
                        }
                    }
                }
                renderTopToolbarCustomActions={
                    () => {
                        if (enableExportToolbar) {
                            return (
                                <ReportExportDropdown
                                    onCSV={onCSVExport}
                                    onExcel={handleExcelExport}
                                    onPDF={handlePDFExport}
                                    onImage={handleImageExport}
                                    disabled={exportDisabled}
                                    label="Download"
                                />
                            );
                        }
                        return (<></>);
                    }
                }
            />
        </div>
    );
}

export default ReactTableWrapper;
